import { Box, FormHelperText, MenuItem, TextField } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useTranslation } from 'react-i18next';
import { StyledInputLabel } from '../../../components/inputs/InputLabel';
import { MultiSelectChips } from '../../../components/inputs/MultiSelectChips';
import { AccessibleSelect } from '../../../components/inputs/AccessibleSelect';
import { MasterListJournalRequestBody } from '../../../types/MasterList.types';
import { ErrorObject } from '../../../types/errorhandling.types';
import { AccessibleAutocomplete } from '../../../components/inputs/AccessibleAutocomplete';
import { translateFieldError } from '../../../utils/apiErrors';

export type MasterListJournalFormData =
  MasterListJournalRequestBody['delta'] & { change_message: string };

interface MasterListJournalFormProps {
  value: MasterListJournalFormData;
  disabled: boolean;
  /** Validation errors returned by the API, keyed by backend field name. */
  errors?: ErrorObject;
  licenseOptions: string[];
  publisherOptions: string[];
  publishingModelOptions: string[];
  onFieldChange: (event: { target: { name?: string; value: unknown } }) => void;
  onLicenseChange: (event: SelectChangeEvent<string[]>) => void;
}

export function MasterListJournalForm({
  value,
  disabled,
  errors,
  licenseOptions,
  publisherOptions,
  publishingModelOptions,
  onFieldChange,
  onLicenseChange,
}: MasterListJournalFormProps) {
  const { t } = useTranslation();
  const {
    name,
    publisher,
    imprint,
    licenses,
    publishing_model,
    issn_e,
    issn_p,
    subjects,
    url,
    change_message,
  } = value;

  const fieldError = (field: string) => {
    const message = errors?.[field]?.[0];
    return message ? translateFieldError(t, message) : undefined;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <Box>
        <StyledInputLabel htmlFor="journal-name">
          {t('masterList.journalName')} <span aria-hidden="true">*</span>
        </StyledInputLabel>
        <TextField
          fullWidth
          required
          id="journal-name"
          name="name"
          value={name}
          onChange={(e) => onFieldChange(e)}
          disabled={disabled}
          error={!!fieldError('name')}
          helperText={fieldError('name')}
        />
      </Box>

      <Box>
        <StyledInputLabel id="publisher-label" htmlFor="publisher">
          {t('agreements.publisher')} <span aria-hidden="true">*</span>
        </StyledInputLabel>
        <AccessibleAutocomplete
          id="publisher"
          options={publisherOptions}
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                required: true,
                'aria-labelledby': 'publisher-label',
              }}
              error={!!fieldError('publisher')}
              helperText={fieldError('publisher')}
            />
          )}
          sx={{ width: '100%' }}
          value={publisher || null}
          onChange={(_, value) =>
            onFieldChange({
              target: { name: 'publisher', value: value ?? '' },
            })
          }
          disabled={disabled}
          isOptionEqualToValue={(option, value) =>
            option === value || value === ''
          }
        />
      </Box>

      <Box>
        <StyledInputLabel htmlFor="imprint">
          {t('masterList.columnImprint')}
        </StyledInputLabel>
        <TextField
          fullWidth
          id="imprint"
          name="imprint"
          value={imprint}
          onChange={(e) => onFieldChange(e)}
          disabled={disabled}
          error={!!fieldError('imprint')}
          helperText={fieldError('imprint')}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: '8px',
        }}
      >
        <Box>
          <StyledInputLabel
            id="licenses-label-master-list"
            htmlFor="licenses-input"
          >
            {t('masterList.columnLicenseOption')}{' '}
            <span aria-hidden="true">*</span>
          </StyledInputLabel>
          <MultiSelectChips
            id="licenses"
            name="licenses"
            labelId="licenses-label-master-list"
            value={licenses ?? []}
            options={licenseOptions.map((option) => ({
              value: option,
              label: option,
            }))}
            onChange={onLicenseChange}
            disabled={disabled}
          />
          {fieldError('licenses') && (
            <FormHelperText error>{fieldError('licenses')}</FormHelperText>
          )}
        </Box>

        <Box>
          <AccessibleSelect
            id="publishing_model"
            label={
              <>
                {t('masterList.columnPublishingModel')}{' '}
                <span aria-hidden="true">*</span>
              </>
            }
            name="publishing_model"
            value={publishing_model}
            onChange={(e) => onFieldChange(e)}
            fullWidth
            required
            disabled={disabled}
            error={!!fieldError('publishing_model')}
          >
            {publishingModelOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </AccessibleSelect>
          {fieldError('publishing_model') && (
            <FormHelperText error>
              {fieldError('publishing_model')}
            </FormHelperText>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: '8px',
        }}
      >
        <Box>
          <StyledInputLabel htmlFor="issn_e">ISSN-E</StyledInputLabel>
          <TextField
            fullWidth
            id="issn_e"
            name="issn_e"
            value={issn_e}
            onChange={(e) => onFieldChange(e)}
            disabled={disabled}
            error={!!fieldError('issn_e')}
            helperText={fieldError('issn_e')}
          />
        </Box>

        <Box>
          <StyledInputLabel htmlFor="issn_p">ISSN-P</StyledInputLabel>
          <TextField
            fullWidth
            id="issn_p"
            name="issn_p"
            value={issn_p}
            onChange={(e) => onFieldChange(e)}
            disabled={disabled}
            error={!!fieldError('issn_p')}
            helperText={fieldError('issn_p')}
          />
        </Box>
      </Box>

      <Box>
        <StyledInputLabel htmlFor="subjects">
          {t('masterList.columnSubject')}
        </StyledInputLabel>
        <TextField
          fullWidth
          id="subjects"
          name="subjects"
          value={subjects}
          onChange={(e) => onFieldChange(e)}
          disabled={disabled}
          error={!!fieldError('subjects')}
          helperText={fieldError('subjects')}
        />
      </Box>

      <Box>
        <StyledInputLabel htmlFor="url">
          {t('agreements.journalURL')} <span aria-hidden="true">*</span>
        </StyledInputLabel>
        <TextField
          required
          inputProps={{
            pattern: 'https://.*',
            title: t('masterList.urlHttpsHint') as string,
          }}
          type="url"
          placeholder="https://www.example.com"
          fullWidth
          id="url"
          name="url"
          value={url}
          onChange={(e) => onFieldChange(e)}
          disabled={disabled}
          error={!!fieldError('url')}
          helperText={fieldError('url')}
        />
      </Box>

      <Box
        sx={{
          backgroundColor: 'var(--color-greyish-blue)',
          borderRadius: 1,
          p: 12,
          mt: 4,
        }}
      >
        <StyledInputLabel htmlFor="change_message">
          Change message (Internal) <span aria-hidden="true">*</span>
        </StyledInputLabel>
        <TextField
          fullWidth
          id="change_message"
          name="change_message"
          value={change_message}
          onChange={(e) => onFieldChange(e)}
          disabled={disabled}
          error={!!fieldError('change_message')}
          helperText={fieldError('change_message')}
        />
      </Box>
    </Box>
  );
}
